import PersonSearchOutlined from '@mui/icons-material/PersonSearchOutlined';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import EmptyStateOverview from 'v2/components/EmptyStateOverview/EmptyStateOverview';
import useModalContext from 'v2/components/Modal/useModalContext';
import OverviewContent from 'v2/components/OverviewContent/OverviewContent';
import PageTabs from 'v2/components/PageTabs/PageTabs';
import SearchAndFilters from 'v2/components/SearchAndFilters/SearchAndFilters';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { WIZARD_MODAL_DEFAULT_OPTIONS } from 'v2/constants';
import useAllVisitors from 'v2/hooks/useAllVisitors/useAllVisitors';
import { createUsersTabs } from 'v2/pages/Users/createUserTabs';
import AllUsersOverview from './components/AllUsersOverview';
import useAllUsersSelection from './components/useAllUsersSelection';
import { useUsersSearchAndFilters } from './useUsersSearchAndFilters';
import AddUserWizard from './wizards/AddUserWizard';

const UsersOverview: React.FC = () => {
  const { translation } = useTranslation();
  const { openModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });

  const { allVisitors, loadingAllVisitors, allVisitorsCards, loadingAllVisitorsCards } = useAllVisitors({
    handleFetchError
  });

  const handleOpenAddUserWizard = () => openModal(<AddUserWizard />, WIZARD_MODAL_DEFAULT_OPTIONS);

  const { filterStr, setFilterStr, countCaption, clearAllFilters, chips, sections, filteredVisitors } =
    useUsersSearchAndFilters({
      allVisitors,
      allVisitorsCards
    });

  const selection = useAllUsersSelection({ visitors: allVisitors, filteredVisitors });

  const isLoading = loadingAllVisitors || loadingAllVisitorsCards;
  const tabs = createUsersTabs(translation);

  const breadcrumb = {
    title: translation.all_users,
    crumbs: [
      {
        text: translation.users,
        path: '/users'
      }
    ]
  };

  return (
    <>
      <BreadcrumbMeta breadcrumb={breadcrumb} />
      <PageTabs tabs={tabs} />
      <SearchAndFilters
        filterStr={filterStr}
        onFilterStrChange={setFilterStr}
        sections={sections}
        chips={chips}
        countCaption={countCaption}
        actionButton={{ id: 'add-user-button', label: translation.add_user, onClick: handleOpenAddUserWizard }}
        onClearAll={clearAllFilters}
      />
      <OverviewContent
        loading={isLoading}
        hasData={allVisitors.length > 0}
        hasResults={selection.rows.length > 0}
        table={
          <AllUsersOverview visitors={allVisitors} selection={selection} cards={allVisitorsCards} loading={isLoading} />
        }
        noResults={
          <EmptyStateOverview
            icon={PersonSearchOutlined}
            title={translation.no_results}
            content={translation.no_results_search_and_filters_description}
          />
        }
        noData={
          <EmptyStateOverview
            icon={PersonSearchOutlined}
            title={translation.no_users_in_system}
            content={translation.add_first_user_prompt}
            button={{ label: translation.add_user, onClick: handleOpenAddUserWizard }}
          />
        }
      />
    </>
  );
};

export default UsersOverview;
